import { useState, type FormEvent } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, Loader2, Unlock, X } from 'lucide-react';
import { toast } from 'sonner';
import { binder } from '@/api/client';
import { errorText } from '@/lib/permissionError';

/**
 * Sprint-15 (option 2) — Exceptional unlock dialog.
 *
 * Client spec §7.2 requires unlocking to be:
 *   - role-gated (site_admin / regulatory_specialist + platform admins —
 *     enforced on the BE, the button is also hidden in the FE for other roles)
 *   - explicitly justified (free text, min 10 chars, captured in audit)
 *   - audited (DocumentUnlocked event with the justification in newValue)
 *
 * This is intentionally a separate flow from `transitionVersionStatus`'s
 * generic change_reason capture. The previous behavior — reopen-to-draft
 * implicitly unlocking the slot — is now blocked at the BE; users must come
 * through this dialog first.
 */

const MIN_REASON = 10;
const MAX_REASON = 1000;

export interface UnlockDocumentDialogProps {
  binderId: string;
  documentId: string;
  documentName?: string;
  open: boolean;
  onClose: () => void;
  onUnlocked: () => void;
}

export function UnlockDocumentDialog({
  binderId,
  documentId,
  documentName,
  open,
  onClose,
  onUnlocked,
}: UnlockDocumentDialogProps) {
  const qc = useQueryClient();
  const [reason, setReason] = useState('');
  const trimmed = reason.trim();
  const isValid = trimmed.length >= MIN_REASON && trimmed.length <= MAX_REASON;

  const unlock = useMutation({
    mutationFn: () => binder.unlockDocument(binderId, documentId, { reason: trimmed }),
    onSuccess: () => {
      toast.success('Document unlocked. Reason captured in the audit trail.');
      qc.invalidateQueries({ queryKey: ['binder', binderId] });
      qc.invalidateQueries({ queryKey: ['binder-versions', binderId, documentId] });
      setReason('');
      onUnlocked();
      onClose();
    },
    onError: (err) => {
      toast.error(errorText(err, 'Unlock failed'));
    },
  });

  function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (!isValid || unlock.isPending) return;
    unlock.mutate();
  }

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center !m-0 bg-black/60 backdrop-blur-sm p-4 !m-0"
      onClick={(e) => {
        if (e.target === e.currentTarget && !unlock.isPending) onClose();
      }}
    >
      <form onSubmit={handleSubmit} className="card w-full max-w-lg space-y-4 p-6">
        <div className="flex items-start justify-between">
          <div className="min-w-0">
            <h2 className="flex items-center gap-2 text-lg font-semibold text-ink-primary">
              <Unlock className="h-4 w-4 text-amber-600" />
              Unlock document
            </h2>
            <p className="mt-0.5 text-xs text-ink-muted">
              {documentName ? (
                <>
                  Unlocking <span className="font-medium text-ink-primary">{documentName}</span>
                  .{' '}
                </>
              ) : null}
              This action is exceptional and recorded in the audit trail with your justification.
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            disabled={unlock.isPending}
            className="text-ink-muted hover:text-ink-primary disabled:cursor-not-allowed disabled:opacity-40"
            aria-label="Close"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="flex items-start gap-2 rounded border border-amber-200 bg-amber-50 p-3 text-xs text-amber-800">
          <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
          <span>
            Locked documents are normally replaced via supersession (upload a new version). Use
            unlock only when in-place changes are unavoidable — the justification will be reviewed
            during inspections.
          </span>
        </div>

        <div>
          <label className="label text-xs" htmlFor="unlock-reason">
            Justification * <span className="text-ink-muted">(min {MIN_REASON} characters)</span>
          </label>
          <textarea
            id="unlock-reason"
            className="input min-h-[110px]"
            maxLength={MAX_REASON}
            value={reason}
            placeholder="e.g. 'IRB submitted v3.1 with a citation typo; correcting in place per regulatory request, reference IRB-2026-1142.'"
            disabled={unlock.isPending}
            onChange={(e) => setReason(e.target.value)}
            required
          />
          <div className="mt-1 flex justify-between text-[11px] text-ink-muted">
            <span>
              {trimmed.length < MIN_REASON
                ? `${MIN_REASON - trimmed.length} more characters needed`
                : ' '}
            </span>
            <span>
              {trimmed.length} / {MAX_REASON}
            </span>
          </div>
        </div>

        <div className="flex items-center justify-end gap-2">
          <button type="button" className="btn-ghost" onClick={onClose} disabled={unlock.isPending}>
            Cancel
          </button>
          <button type="submit" className="btn-primary" disabled={!isValid || unlock.isPending}>
            {unlock.isPending ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Unlock className="h-4 w-4" />
            )}
            Unlock with justification
          </button>
        </div>
      </form>
    </div>
  );
}
